import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { MatDialog, DialogPosition, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ModalFeedbackReportComponent } from '../../modals/modal-feedback-report/modal-feedback-report.component';

@Component({
  selector: 'app-feedback-report-button',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  templateUrl: './feedback-report-button.component.html',
  styleUrls: ['./feedback-report-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackReportButtonComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  showButton = false;
  /** True when the app footer intersects the viewport (user can see it or is near it). */
  footerInView = true;
  private subscription?: Subscription;
  private footerObserver?: IntersectionObserver;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        this.showButton =
          !!environment.feedbackGloballyEnabled || !!data['feedbackEnabled'];
        this.cdr.markForCheck();
      });

    // Check initial route
    let route = this.activatedRoute;
    while (route.firstChild) route = route.firstChild;
    this.showButton =
      !!environment.feedbackGloballyEnabled ||
      !!route.snapshot.data['feedbackEnabled'];
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    const footer = document.querySelector('app-footer');
    if (!footer || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.footerObserver = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e) return;
        const next = e.isIntersecting;
        if (next !== this.footerInView) {
          this.footerInView = next;
          this.cdr.markForCheck();
        }
      },
      { root: null, threshold: 0, rootMargin: '0px' }
    );
    this.footerObserver.observe(footer);
  }

  ngOnDestroy(): void {
    this.footerObserver?.disconnect();
    this.subscription?.unsubscribe();
  }

  openModal(event: Event): void {
    const trigger = event.currentTarget as HTMLElement | null;
    const position =
      trigger && typeof trigger.getBoundingClientRect === 'function'
        ? this.getDialogPositionNearTrigger(trigger)
        : undefined;

    const ref = this.dialog.open(ModalFeedbackReportComponent, {
      panelClass: ['medium-dialog', 'feedback-report-dialog-pane'],
      width: '380px',
      maxWidth: 'min(380px, calc(100vw - 4rem))',
      maxHeight: 'min(92vh, calc(100vh - 3rem))',
      position,
      autoFocus: 'first-tabbable',
      data: {},
    });

    if (trigger) {
      this.attachFeedbackDialogFixedLayout(ref, trigger);
    }
  }

  /**
   * Un solo aggiornamento geometria + entrata animata (vedi _common.scss): evita più
   * riposizionamenti che creavano l’effetto “salto”.
   */
  private attachFeedbackDialogFixedLayout(
    dialogRef: MatDialogRef<ModalFeedbackReportComponent, unknown>,
    trigger: HTMLElement
  ): void {
    const run = () => {
      const pane = document.querySelector(
        '.cdk-overlay-container .cdk-overlay-pane.feedback-report-dialog-pane'
      ) as HTMLElement | null;
      if (!pane) return false;
      this.applyFeedbackDialogGeometry(trigger, pane);
      requestAnimationFrame(() => {
        pane.classList.add('feedback-report-dialog-pane--enter');
      });
      return true;
    };

    dialogRef
      .afterOpened()
      .pipe(take(1))
      .subscribe(() => {
        requestAnimationFrame(() => {
          if (!run()) {
            setTimeout(() => {
              if (!run()) {
                setTimeout(run, 32);
              }
            }, 0);
          }
        });
      });
  }

  /** Solo position/size/margin sul pane; transform/opacity sono gestiti da CSS. */
  private applyFeedbackDialogGeometry(trigger: HTMLElement, pane: HTMLElement): void {
    const rect = trigger.getBoundingClientRect();
    const inset = 24;
    const gapAboveFab = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(380, vw - inset * 2);

    const fromBottom = vh - rect.top + gapAboveFab;
    const bottom = Math.max(inset, fromBottom);
    const right = Math.max(inset, vw - rect.right + 6);

    pane.style.setProperty('position', 'fixed', 'important');
    pane.style.setProperty('left', 'auto', 'important');
    pane.style.setProperty('top', 'auto', 'important');
    pane.style.setProperty('right', `${right}px`, 'important');
    pane.style.setProperty('bottom', `${bottom}px`, 'important');
    pane.style.setProperty('width', `${w}px`, 'important');
    pane.style.setProperty('max-width', `${w}px`, 'important');
    pane.style.setProperty(
      'max-height',
      `min(92vh, calc(100vh - ${inset * 2}px))`,
      'important'
    );
    pane.style.setProperty('margin', '0', 'important');
  }

  /**
   * Posizione iniziale per MatDialog (GlobalPositionStrategy); il layout finale è raffinato da
   * {@link applyFeedbackDialogGeometry}.
   */
  private getDialogPositionNearTrigger(trigger: HTMLElement): DialogPosition {
    const rect = trigger.getBoundingClientRect();
    const gapAboveFab = 20;
    const inset = 32;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const bottom = viewportH - rect.top + gapAboveFab;
    const fabInsetFromRight = viewportW - rect.right;
    const right = Math.max(inset, fabInsetFromRight);
    return { bottom: `${bottom}px`, right: `${right}px` };
  }
}
